'use client';

import { useState } from "react";
import Link from "next/link";
import { TallerButtonLink } from "@/components/taller/button";
import { LocaleSwitcher } from "@/components/layout/locale-switcher";
import { useTranslations } from "@/i18n/client";

/** Modern sticky Navbar with glass effect and mobile menu. */
export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslations();
  const nav = t('nav');

  const NAV_LINKS = [
    { label: nav.services, href: "/servicios" },
    { label: nav.cases, href: "/casos-exito" },
    { label: nav.about, href: "/acerca-de" },
    { label: nav.contact, href: "/contacto" },
    { label: nav.blog, href: "/blog" },
  ];

  return (
    <>
      <header className="sticky top-0 z-[120] w-full bg-[var(--color-bg)]/80 backdrop-blur-md border-b border-[var(--color-border)]">
        <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center no-underline relative z-[60]">
            <img
              src="/brand/logo-text.png"
              alt="Codiaj"
              className="h-6 md:h-8 w-auto object-contain block shrink-0"
            />
          </Link>

          {/* Desktop nav */}
          <nav
            aria-label="Primary"
            className="hidden md:flex gap-8 font-sans text-[13px] font-medium text-[var(--color-mute)]"
          >
            {NAV_LINKS.map(({ label, href }) => (
              <Link
                key={href}
                href={href}
                className="no-underline transition-colors duration-150 hover:text-[var(--color-ink)]"
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-4 md:gap-6 relative z-[110]">
            <div className="hidden sm:block">
              <LocaleSwitcher />
            </div>
            <TallerButtonLink variant="primary" size="sm" href="/contacto" className="!hidden md:!inline-flex">
              {nav.cta || 'Empieza gratis →'}
            </TallerButtonLink>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden w-10 h-10 flex flex-col items-center justify-center relative bg-transparent border-0 cursor-pointer outline-none z-[130]"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              <div className="w-6 h-5 flex flex-col justify-between relative">
                <div className={`w-full h-0.5 bg-[var(--color-ink)] transition-all duration-300 ${isOpen ? 'absolute top-1/2 -translate-y-1/2 rotate-45' : ''}`} />
                <div className={`w-full h-0.5 bg-[var(--color-ink)] transition-all duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`} />
                <div className={`w-full h-0.5 bg-[var(--color-ink)] transition-all duration-300 ${isOpen ? 'absolute top-1/2 -translate-y-1/2 -rotate-45' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay - Outside header to avoid stacking context issues */}
      <div
        className={`fixed inset-0 z-[100] md:hidden bg-[#0E0E11] transition-all duration-500 ease-in-out ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`}
      >
        <div className="pt-24 pb-10 px-8 flex flex-col h-full">
          <nav className="flex flex-col gap-6">
            {NAV_LINKS.map(({ label, href }) => (
              <Link
                key={href}
                href={href}
                onClick={() => setIsOpen(false)}
                className="font-sans font-bold text-[28px] xs:text-[32px] tracking-tight text-white no-underline hover:text-[var(--color-accent)] transition-colors"
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="mt-auto pt-10 border-t border-white/10 flex flex-col gap-8">
            <div className="flex justify-between items-center">
              <span className="font-mono text-[11px] tracking-widest uppercase text-slate-400">Idioma</span>
              <LocaleSwitcher />
            </div>
            <TallerButtonLink
              variant="primary"
              size="lg"
              href="/contacto"
              className="w-full !py-4"
              onClick={() => setIsOpen(false)}
            >
              {nav.cta || 'Empieza gratis →'}
            </TallerButtonLink>
          </div>
        </div>
      </div>
    </>
  );
}
